// Alert when real-time journal edits/cancels were interrupted and are waiting to be completed (roll-forward, no deletes).
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';

export function JournalSyncRecoveryAlert({ onRecovered }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get('/accounting/journal-sync/pending').then(({ data }) => setPending(data)).catch(() => setPending(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!pending?.count) return null;
  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/accounting/journal-sync/recover');
      const { blocked = [], ...done } = data.recovered || {};
      const n = Object.values(done).reduce((a, b) => a + b, 0);
      if (n) toast.success(`${n} interrupted change(s) completed — no duplicate journals`);
      if (blocked.length) toast.error(`${blocked.length} held for accountant review: ${blocked[0].reason}`);
      load();
      onRecovered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recovery failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-3" data-testid="journal-sync-recovery-alert">
      <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm">
        <p className="font-semibold text-amber-900" data-testid="journal-sync-pending-count">
          {pending.count} accounting change{pending.count > 1 ? 's' : ''} interrupted and waiting to be completed
        </p>
        <p className="text-xs text-amber-800 mt-0.5">
          {pending.rows.slice(0, 5).map((r) => `${r.op} ${r.collection.replace('_', ' ')} ${String(r.id).slice(0, 8)}`).join(' · ')}
        </p>
      </div>
      <Button size="sm" className="rounded-xl" onClick={complete} disabled={busy} data-testid="journal-sync-recover-btn">
        {busy && <Loader2 size={13} className="animate-spin mr-1" />} Complete now
      </Button>
    </div>
  );
}
